// Canonical Cinema Analytics aggregation layer.
//
// This module is byte-identical between the Cortex (client) and Quantum
// (superadmin) portals — asserted by CinemaAnalytics.parity.test.js — so both
// portals present the same cinema truth from the same arithmetic.
//
// It is a PURE normalization/aggregation layer over the governed footprint
// response (GET /api/cinema-market-analysis/:advertiserId/cinema-footprint).
// Every KPI, chart, rail card, and table total on the Cinema Analytics page
// derives from the model built here, with ONE denominator everywhere:
// the number of active venues in the governed footprint list. Nothing in this
// file fabricates data — absent evidence stays absent (empty arrays, null
// aggregates, hasFootprint: false), and rendering layers translate that into
// governed empty states.

import {
  INDEPENDENT_CHAIN_LABEL,
} from "./cinemaChainRecognition";

export const CINEMA_FOOTPRINT_UNSPECIFIED_STATE_KEY = "UNSPECIFIED";
export const CINEMA_FOOTPRINT_UNSPECIFIED_STATE_LABEL = "Unspecified";
export const CINEMA_FOOTPRINT_UNSPECIFIED_EXHIBITOR_LABEL = INDEPENDENT_CHAIN_LABEL;

// Fixed equal-area tile positions for the state-level geographic footprint —
// the standard U.S. tile-grid cartogram arrangement (50 states + DC). Tile
// placement is a fixed presentation convention: it never invents venue
// geography, it only positions REAL state-level counts. States absent from a
// footprint render as inert outline tiles; regions outside this grid (e.g.
// Canadian provinces) are listed separately by the rendering layer rather
// than forced onto a U.S. map.
export const US_STATE_TILE_GRID = Object.freeze({
  AK: { row: 1, col: 1 },
  ME: { row: 1, col: 11 },
  VT: { row: 2, col: 10 },
  NH: { row: 2, col: 11 },
  WA: { row: 3, col: 1 },
  ID: { row: 3, col: 2 },
  MT: { row: 3, col: 3 },
  ND: { row: 3, col: 4 },
  MN: { row: 3, col: 5 },
  IL: { row: 3, col: 6 },
  WI: { row: 3, col: 7 },
  MI: { row: 3, col: 8 },
  NY: { row: 3, col: 9 },
  RI: { row: 3, col: 10 },
  MA: { row: 3, col: 11 },
  OR: { row: 4, col: 1 },
  NV: { row: 4, col: 2 },
  WY: { row: 4, col: 3 },
  SD: { row: 4, col: 4 },
  IA: { row: 4, col: 5 },
  IN: { row: 4, col: 6 },
  OH: { row: 4, col: 7 },
  PA: { row: 4, col: 8 },
  NJ: { row: 4, col: 9 },
  CT: { row: 4, col: 10 },
  CA: { row: 5, col: 1 },
  UT: { row: 5, col: 2 },
  CO: { row: 5, col: 3 },
  NE: { row: 5, col: 4 },
  MO: { row: 5, col: 5 },
  KY: { row: 5, col: 6 },
  WV: { row: 5, col: 7 },
  VA: { row: 5, col: 8 },
  MD: { row: 5, col: 9 },
  DE: { row: 5, col: 10 },
  AZ: { row: 6, col: 2 },
  NM: { row: 6, col: 3 },
  KS: { row: 6, col: 4 },
  AR: { row: 6, col: 5 },
  TN: { row: 6, col: 6 },
  NC: { row: 6, col: 7 },
  SC: { row: 6, col: 8 },
  DC: { row: 6, col: 9 },
  OK: { row: 7, col: 4 },
  LA: { row: 7, col: 5 },
  MS: { row: 7, col: 6 },
  AL: { row: 7, col: 7 },
  GA: { row: 7, col: 8 },
  HI: { row: 8, col: 1 },
  TX: { row: 8, col: 4 },
  FL: { row: 8, col: 9 },
});

export const US_STATE_TILE_GRID_ROWS = 8;
export const US_STATE_TILE_GRID_COLS = 11;

// Display names for the tile grid's abbreviations. Presentation only: the
// state KEY stays the identity everything is aggregated and filtered by, so a
// missing name degrades to the abbreviation rather than renaming a group.
export const US_STATE_NAMES = Object.freeze({
  AK: "Alaska",
  AL: "Alabama",
  AR: "Arkansas",
  AZ: "Arizona",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DC: "District of Columbia",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  IA: "Iowa",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  MA: "Massachusetts",
  MD: "Maryland",
  ME: "Maine",
  MI: "Michigan",
  MN: "Minnesota",
  MO: "Missouri",
  MS: "Mississippi",
  MT: "Montana",
  NC: "North Carolina",
  ND: "North Dakota",
  NE: "Nebraska",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NV: "Nevada",
  NY: "New York",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VA: "Virginia",
  VT: "Vermont",
  WA: "Washington",
  WI: "Wisconsin",
  WV: "West Virginia",
  WY: "Wyoming",
});

export function resolveStateDisplayName(stateKey, fallbackLabel = "") {
  const key = cleanText(stateKey).toUpperCase();
  return US_STATE_NAMES[key] || cleanText(fallbackLabel) || key;
}

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const cleanText = (value) => String(value ?? "").trim();

const normalizeStateKey = (value) => {
  const cleaned = cleanText(value).toUpperCase();
  return cleaned || CINEMA_FOOTPRINT_UNSPECIFIED_STATE_KEY;
};

const normalizeGroupKey = (value) =>
  cleanText(value)
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

// One percentage rule for the whole page: exact share of the governed
// footprint, displayed to one decimal place.
export function computeFootprintPercentage(count, totalCinemas) {
  const numerator = Number(count);
  const denominator = Number(totalCinemas);
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) {
    return null;
  }
  return (numerator / denominator) * 100;
}

export function formatFootprintPercentage(percentage) {
  // Number(null) === 0, so absent evidence must be rejected before coercion —
  // a missing share renders as an explicit dash, never a measured-looking 0%.
  if (percentage === null || percentage === undefined || percentage === "") return "—";
  const value = Number(percentage);
  if (!Number.isFinite(value)) return "—";
  return `${(Math.round(value * 10) / 10).toFixed(1)}%`;
}

// The denominator is named wherever a share is shown, so no chart can imply a
// different base than the governed venue list.
export function describeFootprintDenominator(totalCinemas) {
  const total = Number(totalCinemas);
  if (!Number.isFinite(total) || total <= 0) return "No governed footprint venues";
  return `Share of the ${total}-cinema governed footprint`;
}

// Monogram fallback for exhibitors without approved artwork: initials from the
// exhibitor's real name — an existing-brand fallback, never a fabricated logo.
export function getExhibitorMonogram(label) {
  const words = cleanText(label)
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  if (!words.length) return "•";
  return words
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");
}

// A compact concentration summary, not another scrollable list. Eight rows is
// what sits cleanly beside the state chart at desktop width without either
// column dominating.
export const CINEMA_TOP_CITIES_LIMIT = 8;

function normalizeFootprintVenue(location) {
  const exhibitorLabel =
    cleanText(location.chain) ||
    cleanText(location.circuit) ||
    CINEMA_FOOTPRINT_UNSPECIFIED_EXHIBITOR_LABEL;
  const exhibitorKey =
    normalizeGroupKey(location.chainKey) || normalizeGroupKey(exhibitorLabel) || "INDEPENDENT";
  const stateKey = normalizeStateKey(location.stateOrProvince);
  const latitude = toFiniteNumber(location.latitude);
  const longitude = toFiniteNumber(location.longitude);
  return {
    id: location.id ?? null,
    identityKey: cleanText(location.canonicalIdentityKey) || `title-location-${location.id}`,
    cinemaName: cleanText(location.cinemaName) || "Unnamed cinema",
    exhibitorLabel,
    exhibitorKey,
    addressLine1: cleanText(location.addressLine1) || null,
    city: cleanText(location.city) || null,
    stateKey,
    stateLabel:
      stateKey === CINEMA_FOOTPRINT_UNSPECIFIED_STATE_KEY
        ? CINEMA_FOOTPRINT_UNSPECIFIED_STATE_LABEL
        : stateKey,
    postalCode: cleanText(location.postalCode) || null,
    marketLabel: cleanText(location.dma) || null,
    geocodeStatus: cleanText(location.geocodeStatus).toLowerCase() || "pending",
    geoFenceReady: location.geoFenceReady === true,
    hasCoordinates: latitude !== null && longitude !== null,
    latitude,
    longitude,
    radiusOverrideMiles: toFiniteNumber(location.radiusOverrideMiles),
    executionRadiusMiles: toFiniteNumber(location.executionRadiusMiles),
  };
}

function aggregateBy(venues, totalCinemas, keyOf, labelOf) {
  const groups = new Map();
  for (const venue of venues) {
    const key = keyOf(venue);
    if (!groups.has(key)) {
      groups.set(key, { key, label: labelOf(venue), cinemaCount: 0 });
    }
    groups.get(key).cinemaCount += 1;
  }
  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      percentage: computeFootprintPercentage(group.cinemaCount, totalCinemas),
    }))
    .sort(
      (a, b) => b.cinemaCount - a.cinemaCount || a.label.localeCompare(b.label)
    );
}

// Canonical footprint presentation order: dominant exhibitor first, then
// state, city, and cinema name. The source workbook carries no governed
// ordering of its own (row numbers are never identity), so this deliberate
// default keeps both portals and every module aligned.
function compareCanonicalVenues(a, b, exhibitorRankByKey) {
  const rankA = exhibitorRankByKey.get(a.exhibitorKey) ?? Number.MAX_SAFE_INTEGER;
  const rankB = exhibitorRankByKey.get(b.exhibitorKey) ?? Number.MAX_SAFE_INTEGER;
  if (rankA !== rankB) return rankA - rankB;
  return (
    a.stateLabel.localeCompare(b.stateLabel) ||
    String(a.city || "").localeCompare(String(b.city || "")) ||
    a.cinemaName.localeCompare(b.cinemaName) ||
    a.identityKey.localeCompare(b.identityKey)
  );
}

export function buildCinemaFootprintAnalyticsModel(footprintState) {
  const exists = Boolean(footprintState?.exists);
  const rawLocations = Array.isArray(footprintState?.locations)
    ? footprintState.locations
    : [];

  // Active rows only; a duplicated governed identity must never inflate a
  // denominator, so identical identity keys collapse to one venue.
  const seenIdentityKeys = new Set();
  const unordered = [];
  for (const location of rawLocations) {
    if (!location || location.isActive === false) continue;
    const venue = normalizeFootprintVenue(location);
    if (seenIdentityKeys.has(venue.identityKey)) continue;
    seenIdentityKeys.add(venue.identityKey);
    unordered.push(venue);
  }

  const totalCinemas = unordered.length;
  const exhibitors = aggregateBy(
    unordered,
    totalCinemas,
    (venue) => venue.exhibitorKey,
    (venue) => venue.exhibitorLabel
  ).map((group) => ({
    ...group,
    isIndependent: group.label === CINEMA_FOOTPRINT_UNSPECIFIED_EXHIBITOR_LABEL,
  }));
  // Same venue rows the state counts are built from, indexed once so the
  // per-state exhibitor mix is a partition of the footprint rather than a
  // second pass over the source response.
  const venuesByState = new Map();
  for (const venue of unordered) {
    if (!venuesByState.has(venue.stateKey)) venuesByState.set(venue.stateKey, []);
    venuesByState.get(venue.stateKey).push(venue);
  }
  const states = aggregateBy(
    unordered,
    totalCinemas,
    (venue) => venue.stateKey,
    (venue) => venue.stateLabel
  ).map((group) => {
    const stateVenues = venuesByState.get(group.key) || [];
    return {
      ...group,
      isUnspecified: group.key === CINEMA_FOOTPRINT_UNSPECIFIED_STATE_KEY,
      tile: US_STATE_TILE_GRID[group.key] || null,
      stateName: group.key === CINEMA_FOOTPRINT_UNSPECIFIED_STATE_KEY
        ? CINEMA_FOOTPRINT_UNSPECIFIED_STATE_LABEL
        : resolveStateDisplayName(group.key, group.label),
      // Which exhibitors make up this state's count. The aggregation is the
      // same function the footprint-wide exhibitor list uses, over this
      // state's own venues, so `sum(exhibitors.cinemaCount) === cinemaCount`
      // holds by construction. The DENOMINATOR is this state's total, never
      // the footprint total — a state share and a footprint share answer
      // different questions and must not be confused for one another.
      exhibitors: aggregateBy(
        stateVenues,
        stateVenues.length,
        (venue) => venue.exhibitorKey,
        (venue) => venue.exhibitorLabel
      ).map((exhibitor) => ({
        ...exhibitor,
        isIndependent:
          exhibitor.label === CINEMA_FOOTPRINT_UNSPECIFIED_EXHIBITOR_LABEL,
      })),
    };
  });

  // City concentration. Same venues, same aggregateBy, same denominator as the
  // state chart — one geography calculation path, two readings of it.
  //
  // The key carries the state because a city NAME is not unique: Glendale AZ
  // and Glendale CA are different places, and merging them would overstate
  // concentration in a chart whose entire job is to report concentration. The
  // label says so too, since the state chart sits directly beside this one.
  const venuesWithCity = unordered.filter((venue) => venue.city);
  const cities = aggregateBy(
    venuesWithCity,
    totalCinemas,
    (venue) => `${normalizeGroupKey(venue.city)}|${venue.stateKey}`,
    (venue) =>
      venue.stateKey === CINEMA_FOOTPRINT_UNSPECIFIED_STATE_KEY
        ? venue.city
        : `${venue.city}, ${venue.stateLabel}`
  );
  const topCities = cities.slice(0, CINEMA_TOP_CITIES_LIMIT);
  const hiddenCities = cities.slice(CINEMA_TOP_CITIES_LIMIT);

  const venuesWithMarket = unordered.filter((venue) => venue.marketLabel);
  const markets = aggregateBy(
    venuesWithMarket,
    totalCinemas,
    (venue) => normalizeGroupKey(venue.marketLabel),
    (venue) => venue.marketLabel
  );

  const exhibitorRankByKey = new Map(
    exhibitors.map((group, index) => [group.key, index])
  );
  const venues = [...unordered].sort((a, b) =>
    compareCanonicalVenues(a, b, exhibitorRankByKey)
  );

  const topExhibitor = exhibitors[0] || null;
  const topThree = exhibitors.slice(0, 3);
  const topThreeShare =
    exhibitors.length >= 3
      ? topThree.reduce((sum, group) => sum + (group.percentage || 0), 0)
      : null;
  const independentGroup =
    exhibitors.find((group) => group.isIndependent) || null;

  const geocode = {
    withCoordinates: unordered.filter((venue) => venue.hasCoordinates).length,
    geocoded: unordered.filter((venue) => venue.geocodeStatus === "geocoded").length,
    providedBySource: unordered.filter(
      (venue) => venue.geocodeStatus === "provided_by_source"
    ).length,
    pending: unordered.filter((venue) => venue.geocodeStatus === "pending").length,
  };

  const policy = footprintState?.geoFencePolicy || null;
  const radius = {
    unit: policy?.unit || null,
    minimumRadius: toFiniteNumber(policy?.minimumRadius),
    maximumRadius: toFiniteNumber(policy?.maximumRadius),
    defaultRadius: toFiniteNumber(policy?.defaultRadius),
    overrideCount: unordered.filter((venue) => venue.radiusOverrideMiles !== null)
      .length,
  };

  const distinctStates = states.filter((group) => !group.isUnspecified);

  return {
    hasFootprint: exists && totalCinemas > 0,
    totalCinemas,
    venues,
    exhibitors,
    states,
    distinctStateCount: distinctStates.length,
    cities,
    topCities,
    // A footprint can be genuinely flat — 75 cinemas across 70 cities means the
    // "top" list is mostly ties. Reporting what the compact list leaves out is
    // what stops it reading as a ranking when it is really a slice.
    cityCoverage: {
      totalCities: cities.length,
      shownCities: topCities.length,
      hiddenCities: hiddenCities.length,
      hiddenCinemas: hiddenCities.reduce((sum, group) => sum + group.cinemaCount, 0),
      withoutCity: totalCinemas - venuesWithCity.length,
    },
    markets,
    marketCoverage: {
      withMarket: venuesWithMarket.length,
      withoutMarket: totalCinemas - venuesWithMarket.length,
    },
    topExhibitor,
    topThreeShare,
    independentShare: totalCinemas > 0 ? (independentGroup?.percentage ?? 0) : null,
    geocode,
    radius,
    footprintVersion: footprintState?.titleContext?.footprintVersion ?? null,
    footprintTitle: cleanText(footprintState?.titleContext?.title) || null,
    sourceFileName: cleanText(footprintState?.titleContext?.sourceFileName) || null,
    serverCounts: footprintState?.counts || null,
    denominatorLabel: describeFootprintDenominator(totalCinemas),
  };
}

const VENUE_SORTERS = {
  canonical: null,
  cinema: (venue) => venue.cinemaName.toLowerCase(),
  exhibitor: (venue) => venue.exhibitorLabel.toLowerCase(),
  city: (venue) => String(venue.city || "").toLowerCase(),
  state: (venue) => venue.stateLabel.toLowerCase(),
  zip: (venue) => String(venue.postalCode || ""),
  market: (venue) => String(venue.marketLabel || "").toLowerCase(),
  status: (venue) => (venue.geoFenceReady ? 0 : venue.geocodeStatus === "pending" ? 2 : 1),
  radius: (venue) => venue.executionRadiusMiles ?? -1,
};

export const CINEMA_VENUE_SORT_KEYS = Object.freeze(Object.keys(VENUE_SORTERS));

// View-layer filtering/sorting for the complete governed cinema list. Pure and
// non-mutating: the canonical model (and every total derived from it) is
// untouched by any filter, search, or sort the operator applies.
export function filterAndSortCinemaVenues(venues, options = {}) {
  const {
    search = "",
    exhibitorKey = "",
    stateKey = "",
    sortKey = "canonical",
    sortDirection = "asc",
  } = options;
  const query = cleanText(search).toLowerCase();
  let rows = Array.isArray(venues) ? [...venues] : [];
  if (exhibitorKey) {
    rows = rows.filter((venue) => venue.exhibitorKey === exhibitorKey);
  }
  if (stateKey) {
    rows = rows.filter((venue) => venue.stateKey === stateKey);
  }
  if (query) {
    rows = rows.filter((venue) =>
      [
        venue.cinemaName,
        venue.exhibitorLabel,
        venue.addressLine1,
        venue.city,
        venue.stateLabel,
        venue.postalCode,
        venue.marketLabel,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(query))
    );
  }
  const sorter = VENUE_SORTERS[sortKey] || null;
  if (sorter) {
    const direction = sortDirection === "desc" ? -1 : 1;
    rows.sort((a, b) => {
      const valueA = sorter(a);
      const valueB = sorter(b);
      if (valueA < valueB) return -1 * direction;
      if (valueA > valueB) return 1 * direction;
      return a.identityKey.localeCompare(b.identityKey);
    });
  } else if (sortDirection === "desc") {
    rows.reverse();
  }
  return rows;
}
